import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import {usePermissions} from '../../utils/usePermissions';
import {
  Users,
  UserCheck,
  UserMinus,
  TrendingUp,
  PlusCircle,
  Download,
  Search,
  Filter,
  Tag,
  Eye,
  Edit,
  Plus,
  Trash2,
  CheckCircle,
  XCircle
} from 'lucide-react';
import './ProspectsManagement.css';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const ProspectsManagement = ({onNavigate, onLogout, currentPage}) => {
  const {hasPermission} = usePermissions();
  const navigate = useNavigate();

  const [prospects, setProspects] = useState([]);
  const [filteredProspects, setFilteredProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEtat, setFilterEtat] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [showConvertedOnly, setShowConvertedOnly] = useState(false);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [currentProspect, setCurrentProspect] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    age: '',
    email: '',
    telephone: '',
    adresse: '',
    category: 'Person',
    siteWeb: '',
    mapsUrl: '',
    etat: '0-contact',
    source: 'Web',
    dateContact: new Date().toISOString().split('T')[0],
    interesseProduit: '',
    assignedTo: '',
    note: []
  });

  // Import file
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);

  // Pagination
  const [paginationPage, setPaginationPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Stats
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchProspects();
    fetchStats();
  }, [paginationPage, filterEtat, filterCategory, filterSource, showConvertedOnly]);

  useEffect(() => {
    filterProspects();
  }, [searchTerm, prospects]);

  const fetchProspects = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/prospects?page=${paginationPage}&limit=${itemsPerPage}`;

      if (filterEtat) url += `&etat=${filterEtat}`;
      if (filterCategory) url += `&category=${filterCategory}`;
      if (filterSource) url += `&source=${filterSource}`;
      if (showConvertedOnly) url += `&convertedToClient=true`;

      const response = await axios.get(url, {
        headers: {Authorization: `Bearer ${token}`}
      });

      setProspects(response.data.data || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des prospects');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/prospects/stats`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setStats(response.data.data);
    } catch (err) {
      console.error('Erreur stats:', err);
    }
  };

  const filterProspects = () => {
    if (!searchTerm) {
      setFilteredProspects(prospects);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = prospects.filter(prospect =>
      (prospect.nom && prospect.nom.toLowerCase().includes(term)) ||
      (prospect.prenom && prospect.prenom.toLowerCase().includes(term)) ||
      (prospect.email && prospect.email.toLowerCase().includes(term)) ||
      (prospect.telephone && prospect.telephone.includes(term)) ||
      (prospect.adresse && prospect.adresse.toLowerCase().includes(term))
    );
    setFilteredProspects(filtered);
  };

  const handleInputChange = (e) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  };

  const openCreateModal = () => {
    setFormData({
      nom: '',
      prenom: '',
      age: '',
      email: '',
      telephone: '',
      adresse: '',
      category: 'Person',
      siteWeb: '',
      mapsUrl: '',
      etat: '0-contact',
      source: 'Web',
      dateContact: new Date().toISOString().split('T')[0],
      interesseProduit: '',
      assignedTo: '',
      note: []
    });
    setIsEditing(false);
    setShowModal(true);
  };

  const openEditModal = (prospect) => {
    setFormData({
      nom: prospect.nom || '',
      prenom: prospect.prenom || '',
      age: prospect.age || '',
      email: prospect.email || '',
      telephone: prospect.telephone || '',
      adresse: prospect.adresse || '',
      category: prospect.category || 'Person',
      siteWeb: prospect.siteWeb || '',
      mapsUrl: prospect.mapsUrl || '',
      etat: prospect.etat || '0-contact',
      source: prospect.source || 'Web',
      dateContact: prospect.dateContact ? new Date(prospect.dateContact).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      interesseProduit: prospect.interesseProduit || '',
      assignedTo: prospect.assignedTo || '',
      note: prospect.note || []
    });
    setCurrentProspect(prospect);
    setIsEditing(true);
    setShowModal(true);
  };

  const openDetailModal = (prospect) => {
    navigate(`/prospects/${prospect._id}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (isEditing) {
        await axios.put(`${API_BASE_URL}/prospects/${currentProspect._id}`, formData, {
          headers: {Authorization: `Bearer ${token}`}
        });
        setSuccessMessage('Prospect modifié avec succès');
      } else {
        await axios.post(`${API_BASE_URL}/prospects`, formData, {
          headers: {Authorization: `Bearer ${token}`}
        });
        setSuccessMessage('Prospect créé avec succès');
      }
      setShowModal(false);
      fetchProspects();
      fetchStats();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce prospect?')) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/prospects/${id}`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setSuccessMessage('Prospect supprimé avec succès');
      fetchProspects();
      fetchStats();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Erreur lors de la suppression');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToClient = async (id) => {
    if (!window.confirm('Convertir ce prospect en client? Un compte utilisateur sera créé automatiquement.')) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/prospects/${id}/convert`, {}, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setSuccessMessage(`✓ Prospect converti! Login: ${response.data.credentials.login}, Mot de passe: ${response.data.credentials.password}`);
      fetchProspects();
      fetchStats();
      setTimeout(() => setSuccessMessage(''), 10000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la conversion');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setImportFile(e.target.files[0]);
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) return;

    setLoading(true);
    const payload = new FormData();
    payload.append('file', importFile);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/prospects/import/excel`, payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      setImportResults(response.data);
      setSuccessMessage(`${response.data.success.length} prospects importés`);
      fetchProspects();
      fetchStats();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'importation');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pm-backoffice-container">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      <div className="pm-backoffice-content">
        <div className="prospects-management">
          {/* Header Premium */}
          <div className="pm-premium-header">
            <div className="pm-header-title-area">
              <h1>
                Gestion des Prospects
              </h1>
              <p>Gérez vos prospects et convertissez-les en clients</p>
            </div>
            <div className="pm-premium-header-actions">
              {hasPermission('prospects', 'create') && (
                <>
                  <button className="pm-btn-premium-yellow" onClick={openCreateModal}>
                    <PlusCircle size={18} /> NOUVEAU PROSPECT
                  </button>
                  <button className="pm-btn-premium-yellow" onClick={() => setShowImportModal(true)}>
                    <Download size={18} /> Importer Excel
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Statistics Premium Grid */}
          {stats && (
            <div className="pm-premium-stats-grid">
              <div className="pm-premium-stat-card pm-stat-total">
                <div className="pm-stat-card-body">
                  <div className="pm-stat-icon-wrapper pm-icon-blue">
                    <Users size={24} />
                  </div>
                  <div className="pm-stat-info-text">
                    <h3 className="pm-stat-label">TOTAL PROSPECTS</h3>
                    <p className="pm-stat-main-value">{stats.total}</p>
                    <p className="pm-stat-sub-label">Tous les prospects</p>
                  </div>
                </div>
                <div className="pm-stat-sparkline">
                  <svg width="100%" height="40" viewBox="0 0 100 40" preserveAspectRatio="none">
                    <path d="M0 35 Q 25 30, 50 35 T 100 25" fill="none" stroke="#3b82f6" strokeWidth="2" strokeOpacity="0.5" />
                  </svg>
                </div>
              </div>

              <div className="pm-premium-stat-card pm-stat-converted">
                <div className="pm-stat-card-body">
                  <div className="pm-stat-icon-wrapper pm-icon-green">
                    <UserCheck size={24} />
                  </div>
                  <div className="pm-stat-info-text">
                    <h3 className="pm-stat-label">CONVERTIS</h3>
                    <p className="pm-stat-main-value">{stats.converted}</p>
                    <p className="pm-stat-sub-label">Prospects convertis</p>
                  </div>
                </div>
                <div className="pm-stat-sparkline">
                  <svg width="100%" height="40" viewBox="0 0 100 40" preserveAspectRatio="none">
                    <path d="M0 30 Q 20 35, 40 25 T 80 30 T 100 15" fill="none" stroke="#10b981" strokeWidth="2" strokeOpacity="0.5" />
                  </svg>
                </div>
              </div>

              <div className="pm-premium-stat-card pm-stat-not-converted">
                <div className="pm-stat-card-body">
                  <div className="pm-stat-icon-wrapper pm-icon-orange">
                    <UserMinus size={24} />
                  </div>
                  <div className="pm-stat-info-text">
                    <h3 className="pm-stat-label">NON CONVERTIS</h3>
                    <p className="pm-stat-main-value">{stats.notConverted}</p>
                    <p className="pm-stat-sub-label">Pas encore convertis</p>
                  </div>
                </div>
                <div className="pm-stat-sparkline">
                  <svg width="100%" height="40" viewBox="0 0 100 40" preserveAspectRatio="none">
                    <path d="M0 25 Q 30 20, 60 30 T 100 35" fill="none" stroke="#f59e0b" strokeWidth="2" strokeOpacity="0.5" />
                  </svg>
                </div>
              </div>

              <div className="pm-premium-stat-card pm-stat-rate">
                <div className="pm-stat-card-body">
                  <div className="pm-stat-icon-wrapper pm-icon-purple">
                    <TrendingUp size={24} />
                  </div>
                  <div className="pm-stat-info-text">
                    <h3 className="pm-stat-label">TAUX CONVERSION</h3>
                    <p className="pm-stat-main-value">{stats.conversionRate}%</p>
                    <p className="pm-stat-sub-label">Taux de conversion global</p>
                  </div>
                </div>
                <div className="pm-stat-sparkline">
                  <svg width="100%" height="40" viewBox="0 0 100 40" preserveAspectRatio="none">
                    <path d="M0 35 L 20 30 L 40 32 L 60 20 L 80 25 L 100 10" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeOpacity="0.5" />
                  </svg>
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          {error && <div className="alert alert-error">{error}</div>}
          {successMessage && <div className="alert alert-success">{successMessage}</div>}

          {/* Filters Premium Bar */}
          <div className="pm-premium-filters-bar">
            <div className="pm-filters-top-row">
              <div className="pm-search-wrapper">
                <Search className="pm-search-icon-inside" size={18} />
                <input
                  type="text"
                  placeholder="Rechercher (nom, email, téléphone...)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pm-premium-input-search"
                />
              </div>

              <select value={filterEtat} onChange={(e) => setFilterEtat(e.target.value)} className="pm-premium-select">
                <option value="">Tous les états</option>
                <option value="0-contact">0-contact</option>
                <option value="Petit-contact">Petit-contact</option>
                <option value="Relancé">Relancé</option>
                <option value="Prospect chaud">Prospect chaud</option>
                <option value="Client potentiel">Client potentiel</option>
              </select>

              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="pm-premium-select">
                <option value="">🎯 Toutes catégories</option>
                <option value="Industrial">Industrial</option>
                <option value="Industrial Company">Industrial Company</option>
                <option value="Person">Person</option>
                <option value="School">School</option>
                <option value="Government">Government</option>
                <option value="Hospital">Hospital</option>
                <option value="Hotel">Hotel</option>
                <option value="Restaurant">Restaurant</option>
                <option value="Other">Other</option>
              </select>

              <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="pm-premium-select">
                <option value="">Toutes sources</option>
                <option value="Web">Web</option>
                <option value="Referral">Referral</option>
                <option value="Salon">Salon</option>
                <option value="Appel sortant">Appel sortant</option>
              </select>

              <button className="pm-btn-filter-toggle">
                <Filter size={18} /> Filtres
              </button>
            </div>
            <div className="pm-filters-bottom-row">
              <label className="pm-premium-checkbox-label">
                <input
                  type="checkbox"
                  checked={showConvertedOnly}
                  onChange={(e) => setShowConvertedOnly(e.target.checked)}
                />
                Convertis uniquement
              </label>
            </div>
          </div>

          {/* Table Premium */}
          <div className="pm-premium-table-container">
            {loading ? (
              <div className="pm-loading-premium">
                <LoadingSpinner message="Chargement des prospects..." />
              </div>
            ) : (
              <table className="pm-premium-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Catégorie</th>
                    <th>État</th>
                    <th>Source</th>
                    <th>Date d'ajout</th>
                    <th>Insertion Système</th>
                    <th>Converti</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProspects.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="no-data">Aucun prospect trouvé</td>
                    </tr>
                  ) : (
                    filteredProspects.map((prospect, index) => (
                      <tr key={prospect._id}>
                        <td><span className="pm-row-index">{(paginationPage - 1) * itemsPerPage + index + 1}</span></td>
                        <td>
                          <div className="pm-row-name">{prospect.nom} {prospect.prenom}</div>
                        </td>
                        <td><span className="pm-row-email">{prospect.email || '-'}</span></td>
                        <td>{prospect.telephone || '-'}</td>
                        <td>
                          <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                            <Tag size={14} color="#94a3b8" />
                            <span>{prospect.category}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`pm-premium-badge ${
                            prospect.etat === '0-contact' ? 'pm-badge-new' :
                            prospect.etat === 'Petit-contact' ? 'pm-badge-contacted' :
                            prospect.etat === 'Relancé' ? 'pm-badge-proposition' :
                            'pm-badge-hot'
                          }`}>
                            {prospect.etat}
                          </span>
                        </td>
                        <td>{prospect.source}</td>
                        <td>{prospect.dateContact ? new Date(prospect.dateContact).toLocaleDateString('fr-FR') : '-'}</td>
                        <td style={{fontSize: '12px', color: '#6b7280', fontStyle: 'italic'}}>
                          {prospect.createdAt ? new Date(prospect.createdAt).toLocaleDateString('fr-FR', {
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          }) : '-'}
                        </td>
                        <td>
                          {prospect.convertedToClient ? (
                            <span className="pm-status-oui"><CheckCircle size={14} /> Oui</span>
                          ) : (
                            <span className="pm-status-non"><XCircle size={14} /> Non</span>
                          )}
                        </td>
                        <td>
                          <div className="pm-premium-actions-cell">
                            <button className="pm-action-btn pm-btn-view-blue" onClick={() => openDetailModal(prospect)} title="Détails">
                              <Eye size={16} />
                            </button>
                            {hasPermission('prospects', 'update') && (
                              <>
                                <button className="pm-action-btn pm-btn-edit-yellow" onClick={() => openEditModal(prospect)} title="Modifier">
                                  <Edit size={16} />
                                </button>
                                {!prospect.convertedToClient && (
                                  <button className="pm-action-btn pm-btn-add-green" onClick={() => handleConvertToClient(prospect._id)} title="Convertir en client">
                                    <Plus size={16} />
                                  </button>
                                )}
                              </>
                            )}
                            {hasPermission('prospects', 'delete') && (
                              <button className="pm-action-btn pm-btn-delete-red" onClick={() => handleDelete(prospect._id)} title="Supprimer">
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination Premium */}
          <div className="pm-premium-pagination-area">
            <div className="pm-page-size-selector">
              Afficher
              <select className="pm-premium-select" style={{minWidth: 'auto', padding: '4px 8px'}}>
                <option>10</option>
                <option>25</option>
                <option>50</option>
              </select>
              éléments
            </div>
            <div className="pm-pagination-controls">
              <button
                onClick={() => setPaginationPage(prev => Math.max(prev - 1, 1))}
                disabled={paginationPage === 1}
                className="pm-btn-pagin"
              >
                Précédent
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  className={`pm-btn-pagin ${paginationPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setPaginationPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setPaginationPage(prev => Math.min(prev + 1, totalPages))}
                disabled={paginationPage === totalPages}
                className="pm-btn-pagin"
              >
                Suivant
              </button>
            </div>
          </div>

          {/* Footer Premium */}
          <div className="pm-premium-footer">
            <div className="last-update">Dernière mise à jour : {new Date().toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
            <div className="pm-system-status">
              <span>Statut du système :</span>
              <span className="pm-status-dot"></span>
              <span style={{color: '#22c55e', fontWeight: 600}}>En ligne</span>
            </div>
          </div>

          {/* Modal Create/Edit Premium */}
          {showModal && (
            <div className="modal-overlay">
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{isEditing ? <><Edit size={24} /> Modifier le Prospect</> : <><PlusCircle size={24} /> Nouveau Prospect</>}</h2>
                  <button className="btn-close" onClick={() => setShowModal(false)}><XCircle size={24} /></button>
                </div>
                <form onSubmit={handleSubmit} className="modal-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Nom</label>
                      <input
                        type="text"
                        name="nom"
                        value={formData.nom}
                        onChange={handleInputChange}
                        placeholder="Nom de famille"
                      />
                    </div>
                    <div className="form-group">
                      <label>Prénom</label>
                      <input
                        type="text"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleInputChange}
                        placeholder="Prénom"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="exemple@email.com"
                      />
                    </div>
                    <div className="form-group">
                      <label>Téléphone</label>
                      <input
                        type="text"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleInputChange}
                        placeholder="06 00 00 00 00"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Âge</label>
                      <input
                        type="number"
                        name="age"
                        value={formData.age}
                        onChange={handleInputChange}
                        min="0"
                        max="150"
                      />
                    </div>
                    <div className="form-group">
                      <label>Catégorie</label>
                      <select name="category" value={formData.category} onChange={handleInputChange}>
                        <option value="Person">Person</option>
                        <option value="Industrial">Industrial</option>
                        <option value="Industrial Company">Industrial Company</option>
                        <option value="School">School</option>
                        <option value="Government">Government</option>
                        <option value="Hospital">Hospital</option>
                        <option value="Hotel">Hotel</option>
                        <option value="Restaurant">Restaurant</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Adresse</label>
                    <input
                      type="text"
                      name="adresse"
                      value={formData.adresse}
                      onChange={handleInputChange}
                      placeholder="Adresse complète"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Site Web</label>
                      <input
                        type="text"
                        name="siteWeb"
                        value={formData.siteWeb}
                        onChange={handleInputChange}
                        placeholder="https://example.com"
                      />
                    </div>
                    <div className="form-group">
                      <label>Google Maps URL</label>
                      <input
                        type="text"
                        name="mapsUrl"
                        value={formData.mapsUrl}
                        onChange={handleInputChange}
                        placeholder="https://maps.google.com/..."
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>État</label>
                      <select name="etat" value={formData.etat} onChange={handleInputChange}>
                        <option value="0-contact">0-contact</option>
                        <option value="Petit-contact">Petit-contact</option>
                        <option value="Relancé">Relancé</option>
                        <option value="Prospect chaud">Prospect chaud</option>
                        <option value="Client potentiel">Client potentiel</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Source</label>
                      <select name="source" value={formData.source} onChange={handleInputChange}>
                        <option value="Web">Web</option>
                        <option value="Referral">Referral</option>
                        <option value="Salon">Salon</option>
                        <option value="Appel sortant">Appel sortant</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Date de Contact</label>
                      <input
                        type="date"
                        name="dateContact"
                        value={formData.dateContact}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Intéressé par</label>
                      <input
                        type="text"
                        name="interesseProduit"
                        value={formData.interesseProduit}
                        onChange={handleInputChange}
                        placeholder="Produit ou service"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Assigné à</label>
                    <input
                      type="text"
                      name="assignedTo"
                      value={formData.assignedTo}
                      onChange={handleInputChange}
                      placeholder="Commercial responsable"
                    />
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                      Annuler
                    </button>
                    <button type="submit" className="btn-submit" disabled={loading}>
                      {loading ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal Import */}
          {showImportModal && (
            <div className="modal-overlay">
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>📥 Importer des Prospects</h2>
                  <button className="btn-close" onClick={() => setShowImportModal(false)}>×</button>
                </div>
                <div className="import-instructions">
                  <h3>Formats acceptés (Excel/CSV):</h3>

                  <div style={{marginBottom: '15px'}}>
                    <strong>Format 1 - Colonnes séparées (Recommandé):</strong>
                    <p>Colonnes: Name, Category, Location, Phone, Email, Website, GoogleMapsURL</p>
                  </div>

                  <div style={{marginBottom: '15px'}}>
                    <strong>Format 2 - Une seule colonne avec valeurs séparées par virgules:</strong>
                    <p>Exemple: "Solar Tech,Industrial,Tunis,12345678,contact@solar.tn,https://solar.tn,https://maps..."</p>
                  </div>

                  <p><strong>Colonnes/Valeurs:</strong></p>
                  <ul>
                    <li><strong>Name</strong> (requis) - Nom du prospect</li>
                    <li><strong>Category</strong> - Industrial, Industrial Company, Person, School, Government, Hospital, Hotel, Restaurant, Other
                      <br /><small style={{color: '#666'}}>⚠️ Si non valide, sera enregistré comme "Other"</small>
                    </li>
                    <li><strong>Location</strong> - Adresse</li>
                    <li><strong>Phone</strong> - Numéro de téléphone</li>
                    <li><strong>Email</strong> - Adresse email (unique)</li>
                    <li><strong>Website</strong> - Site web</li>
                    <li><strong>GoogleMapsURL</strong> - URL Google Maps</li>
                  </ul>
                  <p style={{color: '#666', fontSize: '13px', marginTop: '10px'}}>
                    💡 Le système détecte automatiquement le format utilisé
                  </p>
                </div>
                <form onSubmit={handleImport} className="import-form">
                  <div className="form-group">
                    <label>Fichier Excel/CSV</label>
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileChange}
                      required
                    />
                  </div>

                  {importResults && (
                    <div className="import-results">
                      <h3>Résultats de l'importation:</h3>
                      <p className="success-count">✓ {importResults.success?.length || 0} prospects importés</p>
                      {importResults.errors && importResults.errors.length > 0 && (
                        <div className="errors-section">
                          <p className="error-count">✗ {importResults.errors.length} erreurs</p>
                          <ul className="error-list">
                            {importResults.errors.slice(0, 5).map((err, idx) => (
                              <li key={idx}>Ligne {err.row}: {err.error}</li>
                            ))}
                          </ul>
                          {importResults.errors.length > 5 && (
                            <p className="more-errors">... et {importResults.errors.length - 5} autres erreurs</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="modal-footer">
                    <button type="button" className="btn-cancel" onClick={() => setShowImportModal(false)}>
                      Fermer
                    </button>
                    <button type="submit" className="btn-submit" disabled={loading || !importFile}>
                      {loading ? 'Importation...' : 'Importer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProspectsManagement;
